/**
 * Spanish for the console and the phone, ported from the V6 design's own aithora-es.js
 * (aithora-design-v6/aithora-es.js, our design file). The design translates what is on
 * the screen, not a key per string: an exact-match dictionary first, then regex rules for
 * composite lines ("Caída · …", "Revisar a … pulso lento"), then whole-word phrase swaps
 * inside longer lines. English stays the default; nothing here runs until Spanish is picked.
 *
 * What is never translated (the guards, each with a test in test/i18n-es.test.ts):
 *   - a person's name: names the apps have seen in a "name" field from the server are
 *     protected outright (protectNames), and a line shaped like a name ("Wing Allen",
 *     "K. Ruiz") is never touched by a rule or a phrase swap;
 *   - phrase swaps only replace whole words, so "Wing A" never turns "Wing Allen" into
 *     "Ala Allen";
 *   - a rule's captured parts (names, numbers, places) are passed through untouched.
 * The screens add their own: the console skips anything under translate="no" and the
 * phone's Text skips noTranslate (Truth Ledger text, Atlas answers, messages that go to a
 * family or a doctor, and resident and staff names where a screen draws them).
 *
 * Clinical Spanish follows the design's dictionary exactly and is pending nurse review.
 */

export type EsRule = [RegExp, (m: RegExpMatchArray) => string | null | undefined];

export interface EsEngine {
  /** Exact matches, English to Spanish. */
  D: Record<string, string>;
  /** Patterns for composite lines, tried in order. */
  R: EsRule[];
  /** Phrase swaps inside longer lines. */
  P: [string, string][];
  /** Regex swaps inside longer lines (after the phrases). */
  X: [RegExp, string][];
  /** A word whose Spanish depends on the box around it (the console's DOM pass reads it). */
  ctx: Record<string, [RegExp, string][]>;
  /** A shorter Spanish label for the side nav. */
  navShort: Record<string, string>;
  norm(s: string): string;
  add(o: Record<string, string>): void;
  rule(re: RegExp, fn: EsRule[1]): void;
  phrase(o: Record<string, string>): void;
  /** The Spanish for a whole line, or null when there is none (the line stays English). */
  tr(t: string | null | undefined): string | null;
  /** Names that must never be translated (people the server named). */
  protectNames(names: Iterable<string | null | undefined>): void;
  /** True when this exact text is a protected name or is shaped like one. */
  isName(t: string): boolean;
  _n: Record<string, string> | null;
  _p: [string, string][] | null;
  _split: boolean;
  _names: Set<string>;
}

// English words that give a half-swapped line away: the design leaves such a line in English.
const ENGLISH_LEFT = /\b(the|and|of|to|is|for|with|you|your|not|on|in|at|by|from|it|this|that|are|was|has|have|be|who|what|when|until|before|after|every|been)\b/;

// "Harold Lee", "Dr. Patel", "K. Ruiz", "Mary-Ann O'Neil", "Wing Allen": two to four capitalised
// words and nothing else. A UI label shaped like this ("Care Staff", "Truth Ledger") is still
// translated by an exact dictionary entry; only rules and phrase swaps are refused.
const NAME_SHAPE = /^(?:(?:Dr|Mr|Mrs|Ms|Mx)\.? )?(?:[A-Z]\. )?[A-Z][A-Za-z'’-]+(?: (?:[A-Z]\.|[A-Z][A-Za-z'’-]+)){1,3}$|^[A-Z]\. [A-Z][A-Za-z'’-]+$/;

const TAILS: [string, string][] = [
  [' · SAMPLE', ' · MUESTRA'],
  [' · MODEL', ' · MODELO'],
  [' ·', ' ·'],
  [':', ':'],
  ['…', '…'],
  [' →', ' →'],
];

const isWordChar =(c: string | undefined) => !!c && /[A-Za-z0-9À-ÿ]/.test(c);

/** Replace every whole-word occurrence of `k` in `v` (a phrase that starts or ends with a
 *  letter must not start or end inside another word). */
function swapWhole(v: string, k: string, to: string): { v: string; hit: boolean } {
  let out = '';
  let i = 0;
  let hit = false;
  for (;;) {
    const j = v.indexOf(k, i);
    if (j === -1) break;
    const before = v[j - 1];
    const after = v[j + k.length];
    const okStart = !isWordChar(k[0]) || !isWordChar(before);
    const okEnd = !isWordChar(k[k.length - 1]) || !isWordChar(after);
    if (okStart && okEnd) {
      out += v.slice(i, j) + to;
      hit = true;
    } else {
      out += v.slice(i, j + k.length);
    }
    i = j + k.length;
  }
  return { v: out + v.slice(i), hit };
}

export function createEsEngine(): EsEngine {
  const A: EsEngine = {
    D: {},
    R: [],
    P: [],
    X: [],
    ctx: {},
    navShort: {},
    _n: null,
    _p: null,
    _split: false,
    _names: new Set<string>(),
    norm(s) {
      return s
        .replace(/[‘’]/g, "'")
        .replace(/[“”]/g, '"')
        .replace(/\s+/g, ' ')
        .replace(/ [-–·] /g, ' · ')
        .trim();
    },
    add(o) {
      Object.assign(A.D, o);
      A._p = null;
      A._n = null;
    },
    rule(re, fn) {
      A.R.push([re, fn]);
    },
    phrase(o) {
      Object.keys(o).forEach((k) => A.P.push([k, o[k]]));
      A._p = null;
    },
    protectNames(names) {
      for (const n of names) {
        if (typeof n !== 'string') continue;
        const t = n.trim();
        if (t.length < 2 || t.length > 80) continue;
        A._names.add(t);
        A._names.add(A.norm(t));
      }
    },
    isName(t) {
      const s = t.trim();
      return A._names.has(s) || NAME_SHAPE.test(s);
    },
    tr(t) {
      if (!t) return null;
      // A protected name is never translated, even when it is also a dictionary word ("May").
      if (A._names.has(t) || A._names.has(t.trim())) return null;
      if (A.D[t] !== undefined) return A.D[t];
      const nt = A.norm(t);
      if (A._names.has(nt)) return null;
      if (!A._n) {
        A._n = {};
        for (const k in A.D) A._n[A.norm(k)] = A.D[k];
      }
      if (A._n[nt] !== undefined) return A._n[nt];
      // Our screens draw some design lines with the tail in its own element (the SAMPLE
      // badge, a trailing "·"): the design's line with that tail, minus the same tail in Spanish.
      for (const [en, es] of TAILS) {
        const hit = A._n[nt + en];
        if (hit !== undefined && hit.endsWith(es)) return hit.slice(0, hit.length - es.length);
      }
      // A line shaped like a person's name: no rule or phrase may touch it.
      if (NAME_SHAPE.test(nt)) return null;
      for (let i = 0; i < A.R.length; i++) {
        const m = nt.match(A.R[i][0]);
        if (m) {
          const r = A.R[i][1](m);
          if (r) return r;
        }
      }
      if (!A._split && /[.] \S/.test(nt)) {
        const parts = nt.split(/(?<=[.]) (?=\S)/);
        if (parts.length > 1) {
          A._split = true;
          let out: (string | null)[];
          try {
            out = parts.map((s) => A.tr(s));
          } finally {
            A._split = false;
          }
          if (out.every(Boolean)) return out.join(' ');
        }
      }
      if (!A._p) A._p = A.P.slice().sort((a, b) => b[0].length - a[0].length);
      // Names inside a longer line are masked before any swap: a part shaped like a name
      // ("Room 12 · Wing Allen") and every protected name, then put back unchanged.
      const masks: string[] = [];
      const mask = (s: string) => '\u0001' + (masks.push(s) - 1) + '\u0002';
      let v = t
        .split(/( · | - |, |: |; )/)
        .map((seg) => (NAME_SHAPE.test(seg) ? mask(seg) : seg))
        .join('');
      for (const n of A._names) if (n.length >= 3 && v.includes(n)) v = v.split(n).join(mask(n));
      let hit = false;
      for (let i = 0; i < A._p.length; i++) {
        const k = A._p[i][0];
        if (v.indexOf(k) !== -1) {
          const r = swapWhole(v, k, A._p[i][1]);
          v = r.v;
          hit = hit || r.hit;
        }
      }
      for (let i = 0; i < A.X.length; i++) {
        const re = A.X[i][0];
        re.lastIndex = 0;
        if (re.test(v)) {
          re.lastIndex = 0;
          v = v.replace(re, A.X[i][1]);
          hit = true;
        }
      }
      // English left over (outside the names) means a half-swapped line: keep it English.
      if (!hit || ENGLISH_LEFT.test(v)) return null;
      // Unmask (a mask may hold another mask's text, so repeat until none is left).
      for (let guard = 0; guard < 4 && v.includes('\u0001'); guard++) v = v.replace(/\u0001(\d+)\u0002/g, (_m, i: string) => masks[Number(i)]);
      return v;
    },
  };
  return A;
}
